import { useAuth } from '@/hooks/useAuth';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { fmtCentavos, fmtMesAno, fmtMesCurto } from '@/pages/admin/shared';
import { useFaturamentoRealizado } from '@/pages/admin/faturamento-data';

const MESES_EXIBIDOS = 12;

export default function AdminFaturamentoRealizadoPage() {
  const { isSuperuser } = useAuth();
  const { meses, carregando, erro, recarregar } = useFaturamentoRealizado(MESES_EXIBIDOS);

  if (!isSuperuser) {
    return (
      <DashboardLayout>
        <div className="max-w-6xl flex flex-col items-center justify-center py-20">
          <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mb-4">
            <i className="ri-lock-line text-red-600 text-2xl"></i>
          </div>
          <h2 className="text-lg font-bold text-foreground-900 mb-2">Acesso Restrito</h2>
          <p className="text-sm text-foreground-500 text-center max-w-md">
            Esta área é reservada para o superusuário administrador da plataforma. Faça login com as
            credenciais de administrador para acessar.
          </p>
        </div>
      </DashboardLayout>
    );
  }

  const totalReceita = meses.reduce((s, f) => s + f.receita, 0);
  const maxReceita = Math.max(1, ...meses.map((f) => f.receita));
  const novosClientes = meses.reduce((s, f) => s + f.novas, 0);
  const mediaMensal = meses.length > 0 ? Math.round(totalReceita / meses.length) : 0;
  const melhorMes = meses.length > 0 ? Math.max(...meses.map((f) => f.receita)) : 0;
  const semReceita = totalReceita === 0;

  const periodo =
    meses.length > 0
      ? `${fmtMesAno(meses[0].mes)} a ${fmtMesAno(meses[meses.length - 1].mes)}`
      : 'sem período apurado';

  return (
    <DashboardLayout>
      <div className="max-w-6xl">
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-xl md:text-2xl font-bold text-foreground-900">
              Faturamento Realizado
            </h1>
            <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-accent-500 text-background-50">
              Super Admin
            </span>
          </div>
          <p className="text-sm text-foreground-500">
            Receita paga, clientes e movimentação de assinaturas — {periodo}.
          </p>
        </div>

        {carregando ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex items-center gap-3 text-foreground-500">
              <i className="ri-loader-4-line animate-spin text-xl"></i>
              <span className="text-sm">Apurando o faturamento...</span>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-6">
            {erro && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <i className="ri-error-warning-line text-red-600"></i>
                  <span className="text-sm text-red-700">Erro ao apurar o faturamento: {erro}</span>
                </div>
                <button
                  onClick={recarregar}
                  className="whitespace-nowrap inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-red-100 text-red-700 hover:bg-red-200 transition-colors cursor-pointer"
                >
                  <i className="ri-refresh-line"></i>
                  Tentar novamente
                </button>
              </div>
            )}

            {/* Cards resumo */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                {
                  label: 'Receita no período',
                  valor: fmtCentavos(totalReceita),
                  icone: 'ri-money-dollar-circle-line',
                  cor: 'primary',
                },
                {
                  label: 'Média mensal',
                  valor: fmtCentavos(mediaMensal),
                  icone: 'ri-line-chart-line',
                  cor: 'accent',
                },
                {
                  label: 'Melhor mês',
                  valor: fmtCentavos(melhorMes),
                  icone: 'ri-medal-line',
                  cor: 'secondary',
                },
                {
                  label: 'Novas assinaturas',
                  valor: String(novosClientes),
                  icone: 'ri-user-add-line',
                  cor: 'primary',
                },
              ].map((card) => {
                const cores = {
                  primary: 'bg-primary-50 text-primary-600',
                  accent: 'bg-accent-50 text-accent-600',
                  secondary: 'bg-secondary-50 text-secondary-600',
                };
                return (
                  <div
                    key={card.label}
                    className="bg-background-50 border border-background-200/70 rounded-lg p-4"
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <div
                        className={`w-8 h-8 rounded-lg ${cores[card.cor as keyof typeof cores]} flex items-center justify-center`}
                      >
                        <i className={`${card.icone} text-sm`}></i>
                      </div>
                      <p className="text-xs font-medium text-foreground-500">{card.label}</p>
                    </div>
                    <p className="text-xl font-bold text-foreground-900">{card.valor}</p>
                  </div>
                );
              })}
            </div>

            {/*
              Zero é um resultado, não uma falha: a receita nasce do webhook do
              Stripe espelhando faturas pagas. Dizer de onde ela viria evita que
              a tela vazia seja lida como tela quebrada.
            */}
            {semReceita && (
              <div className="flex items-start gap-3 p-4 rounded-lg bg-primary-50/50 border border-primary-200/50">
                <i className="ri-information-line text-primary-600 text-sm mt-0.5"></i>
                <div>
                  <p className="text-sm font-medium text-primary-800">
                    Nenhuma fatura paga no período
                  </p>
                  <p className="text-xs text-primary-600 mt-0.5 leading-relaxed">
                    A receita é apurada sobre as faturas que o webhook do Stripe espelha na tabela{' '}
                    <code>faturas</code>. Enquanto nenhum checkout for concluído, os meses aparecem
                    zerados — e é isso mesmo que está acontecendo.
                  </p>
                </div>
              </div>
            )}

            {/* Gráfico */}
            <div className="bg-background-50 border border-background-200/70 rounded-lg">
              <div className="px-5 py-4 border-b border-background-200/70">
                <h3 className="text-base font-semibold text-foreground-900">
                  Receita mensal realizada
                </h3>
                <p className="text-xs text-foreground-500 mt-0.5">
                  Faturas pagas, no mês do pagamento
                </p>
              </div>
              <div className="p-5 md:p-6">
                <div className="flex items-end gap-2 h-48">
                  {meses.map((f) => {
                    const altura = Math.max((f.receita / maxReceita) * 100, 4);
                    return (
                      <div
                        key={f.mes}
                        className="flex-1 flex flex-col items-center gap-2 min-w-0 h-full justify-end"
                      >
                        <span className="text-[10px] font-semibold text-foreground-700 whitespace-nowrap">
                          {fmtCentavos(f.receita)}
                        </span>
                        <div
                          className="w-full max-w-[48px] rounded-t-md bg-primary-500 transition-all hover:bg-primary-600 cursor-pointer relative group"
                          style={{ height: `${altura}%` }}
                        >
                          <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-foreground-900 text-background-50 text-xs rounded-md px-2 py-1 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                            {f.clientes} clientes · +{f.novas} novas
                          </div>
                        </div>
                        <span className="text-[10px] font-medium text-foreground-500">
                          {fmtMesCurto(f.mes)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Tabela detalhada */}
            <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-background-200/70 bg-background-100/50">
                      <th className="text-left text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                        Mês
                      </th>
                      <th className="text-right text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                        Receita
                      </th>
                      <th className="text-center text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                        Clientes
                      </th>
                      <th className="text-center text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                        Novas
                      </th>
                      <th className="text-center text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                        Cancelamentos
                      </th>
                      <th className="text-center text-xs font-semibold text-foreground-500 px-4 py-3 whitespace-nowrap">
                        Crescimento
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-background-200/50">
                    {meses.length === 0 ? (
                      <tr>
                        <td
                          colSpan={6}
                          className="px-4 py-12 text-center text-sm text-foreground-400"
                        >
                          Nenhum mês apurado.
                        </td>
                      </tr>
                    ) : (
                      meses.map((f, idx) => {
                        const anterior = idx > 0 ? meses[idx - 1].receita : f.receita;
                        const crescimento =
                          anterior > 0 ? ((f.receita - anterior) / anterior) * 100 : 0;
                        return (
                          <tr key={f.mes} className="hover:bg-background-100/30 transition-colors">
                            <td className="px-4 py-3">
                              <span className="text-sm font-medium text-foreground-800">
                                {fmtMesAno(f.mes)}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <span className="text-sm font-semibold text-foreground-800">
                                {fmtCentavos(f.receita)}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className="text-sm text-foreground-600">{f.clientes}</span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              {f.novas > 0 ? (
                                <span className="text-sm text-green-700 font-medium">
                                  +{f.novas}
                                </span>
                              ) : (
                                <span className="text-sm text-foreground-400">—</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-center">
                              {f.cancelamentos > 0 ? (
                                <span className="text-sm text-red-700 font-medium">
                                  -{f.cancelamentos}
                                </span>
                              ) : (
                                <span className="text-sm text-foreground-400">—</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span
                                className={`text-sm font-medium ${crescimento > 0 ? 'text-green-700' : crescimento < 0 ? 'text-red-700' : 'text-foreground-400'}`}
                              >
                                {crescimento > 0 ? '+' : ''}
                                {crescimento.toFixed(1)}%
                              </span>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
